import { Box, Menu, MenuItem } from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  ErrorOutline as WarningIcon,
  ExpandMore as ExpandIcon,
  RestartAlt as ResetIcon,
  FastForward as SkipIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { v2 } from "../ui/v2Tokens";
import { macro } from "../ui/macroTokens";
import {
  BMR_FORMULAS,
  CARB_RANGE,
  FAT_RANGE,
  FIBER_RANGE,
  PROTEIN_RANGE,
  STEPS_LEVELS,
  WORKOUT_DURATIONS,
  defaultFiber,
  getRateLabel,
  getWeeklyRangesForGoal,
  missingBmrInputs,
  type Gender,
  type GoalValue,
} from "../../../../utils/nutritionCalculator";
import { GoalTypes } from "../../../../enums/GoalTypes";
import {
  Card,
  Field,
  NumberInput,
  Pill,
  PrimaryButton,
  SectionTitle,
  Select,
  SURFACE,
  TextButton,
} from "../ui/primitives";
import TdeeBreakdown from "./TdeeBreakdown";
import MacroSliderRow from "./MacroSliderRow";
import { deriveTargets, withGoal, type CalculatorState } from "./calculatorState";
import type { BmrInput } from "../../../../utils/nutritionCalculator";
import {
  CALORIES,
  POINTS,
  energyFigure,
  energyFromInput,
  energyLabel,
  energyToInput,
  energyUnit,
  kcalFromPoints,
  macroToInput,
  macroUnit,
  pointsFromKcal,
  type NutritionDisplayMode,
} from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";

/** An energy figure — `2,235`, or `22.4` in points. */
const fmt = (n: number, mode: NutritionDisplayMode = CALORIES) => (n ? energyFigure(n, mode).text : "—");

/**
 * Carbs filling the remainder always land within rounding distance of the calorie target,
 * so only a bigger gap than this means the macros were set by hand and no longer add up.
 */
const MACRO_GAP_TOLERANCE = 20;

/**
 * The calculator's three stat cells: how much per kg, its calories, its share.
 *
 * In points mode the calorie cell goes: the headline already IS the macro's points, which are its
 * calories over 100, so the cell would only say the same number twice. Grams per kilo stays — it is
 * how a coach prescribes the macro, not an amount of it.
 */
const perKgStats = (perKg: number, kcal: number, pct: number, mode: NutritionDisplayMode = CALORIES) => [
  { label: "ג׳ / ק״ג", value: perKg ? perKg.toFixed(1) : "—" },
  ...(mode === POINTS ? [] : [{ label: "קק״ל", value: kcal ? kcal.toLocaleString("he-IL") : "—" }]),
  { label: "% מסה״כ", value: pct ? `${pct}%` : "—" },
];

const MISSING_LABELS: Record<BmrInput, string> = {
  weight: "משקל",
  height: "גובה",
  age: "גיל",
  bodyFat: "אחוז שומן",
};

/** What is being built here. The only thing it changes is the copy about walking past the panel. */
type Subject = "menu" | "template";

/**
 * Written out per subject rather than assembled from a noun: תפריט is masculine and תבנית is
 * feminine, so the verb and the possessive change with it ("התפריט ייבנה" / "התבנית תיבנה"), and
 * a template string with a swapped noun would produce Hebrew nobody writes.
 */
const SKIP_COPY: Record<Subject, { canContinue: string; footerHint: string; skipTitle: string }> = {
  menu: {
    canContinue: " אפשר להמשיך בלעדיהם — הערכים המומלצים ייגזרו מהתפריט עצמו.",
    footerHint: "ללא נתוני לקוח — הערכים המומלצים ייגזרו מהתפריט",
    skipTitle: "התפריט ייבנה בלי יעדים מחושבים — הערכים המומלצים יהיו מה שיש בתפריט",
  },
  template: {
    canContinue: " אפשר להמשיך בלעדיהם — הערכים המומלצים ייגזרו מהתבנית עצמה.",
    footerHint: "ללא נתוני לקוח — הערכים המומלצים ייגזרו מהתבנית",
    skipTitle: "התבנית תיבנה בלי יעדים מחושבים — הערכים המומלצים יהיו מה שיש בתבנית",
  },
};

type Props = {
  state: CalculatorState;
  onChange: (next: CalculatorState) => void;
  /** confirms the calculator — creates the menu the first time, applies the targets later */
  onConfirm?: () => void;
  confirmLabel?: string;
  /**
   * The other ways out of the calculator on creation — one per menu type that isn't the
   * primary one (daily values only, a PDF the trainer already has).
   */
  secondaryActions?: Array<{ label: string; onClick: () => void }>;
  /** offered once the menu exists: discard the edits and close the calculator */
  onCancel?: () => void;
  /**
   * The ways out WITHOUT a calculation.
   *
   * Offered on creation only, and offered even when the calculator is fully filled in: a trainer
   * who is building a menu they already know the shape of should not have to answer six questions
   * about a formula they are not going to use.
   *
   * A LIST rather than a single callback, because walking past the calculator must not also decide
   * what is being built — skipping used to hard-land on a detailed menu, so a trainer who wanted a
   * PDF menu without a calculation had no way to say so. One entry is pressed straight through
   * (a template can only be one thing); several make the button ask which.
   */
  skipActions?: Array<{ label: string; onClick: () => void }>;
  /** Which noun the "you can walk past this" copy talks about. A menu, unless told otherwise. */
  subject?: Subject;
  /**
   * Whether the ways out need a TDEE. Applying the calculator to a menu that already exists does
   * — "החל יעדים" with nothing calculated would write zeros over real targets. CREATING a menu
   * does not: a calculator left empty is the trainer saying they do not want one, and the menu is
   * created with its targets following its foods instead.
   */
  requireTdee?: boolean;
};

const CalculatorPanel = ({
  state,
  onChange,
  onConfirm,
  confirmLabel = "צור תפריט",
  secondaryActions = [],
  onCancel,
  skipActions = [],
  subject = "menu",
  requireTdee = true,
}: Props) => {
  const skipCopy = SKIP_COPY[subject];
  const mode = useNutritionDisplayMode();
  const inPoints = mode === POINTS;
  // "יעד קלורי יומי" / "יעד נקודות יומי" — the metric's name, wherever the target is named
  // the macros' own figures: grams a day, or their points INSTEAD of grams (fibre stays in grams)
  const macroUnitPerDay = inPoints ? `${macroUnit(mode)} / יום` : "גרם / יום";
  const targetName = inPoints ? `יעד ${energyLabel(mode)} יומי` : "יעד קלורי יומי";
  const [editingTarget, setEditingTarget] = useState(false);
  // the target field while it is being typed in, so a points target keeps "18." (points mode)
  const [targetDraft, setTargetDraft] = useState<string | null>(null);
  // the anchor for the "which kind?" menu, when there is more than one way past the calculator
  const [skipAnchor, setSkipAnchor] = useState<HTMLElement | null>(null);
  const derived = deriveTargets(state);
  const { energy, macros, calculatedTarget, target } = derived;
  const weight = Number(state.weight) || 0;

  const patch = (changes: Partial<CalculatorState>) => onChange({ ...state, ...changes });

  // how far the macros add up above (+) or below (−) the calorie target; 0 when they agree
  const macroGap =
    target > 0 && Math.abs(macros.diffFromTarget) > MACRO_GAP_TOLERANCE
      ? Math.round(macros.diffFromTarget)
      : 0;

  const onGenderChange = (value: string) => {
    const gender = value as Gender;
    // the fiber recommendation follows gender until the trainer moves the slider
    const untouched = state.fiber === defaultFiber(state.gender);
    patch({ gender, fiber: untouched ? defaultFiber(gender) : state.fiber });
  };

  const onGoalChange = (value: string) => onChange(withGoal(state, value as GoalValue));

  const formulaNeedsBodyFat = BMR_FORMULAS.find((f) => f.value === state.formula)?.needsBodyFat;
  // nothing downstream — TDEE, target, macros — exists until these are filled in
  const missing = missingBmrInputs({
    formula: state.formula,
    age: Number(state.age) || 0,
    weight: Number(state.weight) || 0,
    height: Number(state.height) || 0,
    bodyFat: Number(state.bodyFat) || undefined,
  });
  const isMissing = (key: BmrInput) => missing.includes(key);
  /**
   * The one state that traps the trainer on this screen: the formula has nothing to work with AND
   * the caller needs a calculation out of it.
   *
   * Keyed off the missing INPUTS, not off `energy.tdee` — NEAT and EAT fall back to a 70kg body,
   * so an untouched calculator still produces a confident ~700 kcal made of nothing.
   */
  const blocked = requireTdee && missing.length > 0;
  const targetSub = (() => {
    const diff = Math.round(target - energy.tdee);
    // In points the difference is taken between the two figures as PRINTED (22.4 − 16.9 = 5.5),
    // so the sentence never subtracts to a number one rounding step off the target beside it.
    const shownDiff = inPoints
      ? fmt(kcalFromPoints(Math.abs(pointsFromKcal(target) - pointsFromKcal(energy.tdee))), mode)
      : fmt(Math.abs(diff));
    const base =
      diff === 0
        ? "שמירה על TDEE"
        : `TDEE ${fmt(energy.tdee, mode)} ${diff < 0 ? "−" : "+"} ${shownDiff} ${energyUnit(mode)}`;
    return state.customTarget !== null ? `ידני · ${base}` : base;
  })();

  return (
    <Card>
      <Box
        sx={{
          padding: "20px 28px 16px",
          borderBottom: `1px solid ${v2.line}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <Box sx={{ fontSize: "19px", fontWeight: 600, color: v2.ink, letterSpacing: "-.3px" }}>
          מחשבון תזונה
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              letterSpacing: ".1px",
              padding: "4px 12px",
              borderRadius: "20px",
              background: v2.brandWash,
              color: v2.brand,
            }}
          >
            הגדרת יעדים
          </Box>
          {onCancel && (
            <Box
              component="button"
              type="button"
              aria-label="סגור מחשבון"
              onClick={onCancel}
              sx={{
                width: "26px",
                height: "26px",
                borderRadius: v2.rSm,
                border: `1px solid ${v2.line}`,
                background: v2.surface,
                color: v2.ink2,
                fontSize: "16px",
                lineHeight: 1,
                cursor: "pointer",
                fontFamily: "inherit",
                "&:hover": { borderColor: v2.ink3, color: v2.ink },
              }}
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
        {/* ── right column: client data + formula + TDEE + the goal it feeds ── */}
        <Box sx={{ padding: "22px 26px", borderLeft: { md: `1px solid ${v2.line}` } }}>
          <SectionTitle>נתוני לקוח</SectionTitle>
          {/*
            Twelve columns, not four, so BOTH groupings fit the same grid: the four short body
            fields take three columns each, and the three training questions take four each. On a
            four-column grid the training row could only be 2 + 1, which is what left "משך אימון
            ממוצע" stranded on a line of its own.
          */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(6, 1fr)", md: "repeat(12, 1fr)" },
              gap: "10px",
            }}
          >
            <Field label="מגדר" sx={{ gridColumn: { sm: "span 3", md: "span 3" } }}>
              <Select
                ariaLabel="מגדר"
                value={state.gender}
                onChange={onGenderChange}
                options={[
                  { value: "male", label: "זכר" },
                  { value: "female", label: "נקבה" },
                ]}
              />
            </Field>
            <Field label="גיל" required missing={isMissing("age")} sx={{ gridColumn: { sm: "span 3", md: "span 3" } }}>
              <NumberInput
                ariaLabel="גיל"
                value={state.age}
                placeholder="35"
                min={10}
                max={100}
                suffix="שנים"
                onChange={(v) => patch({ age: v === "" ? "" : Number(v) })}
              />
            </Field>
            <Field label="משקל" required missing={isMissing("weight")} sx={{ gridColumn: { sm: "span 3", md: "span 3" } }}>
              <NumberInput
                ariaLabel="משקל"
                value={state.weight}
                placeholder="75"
                min={30}
                max={300}
                step={0.1}
                suffix="ק״ג"
                onChange={(v) => patch({ weight: v === "" ? "" : Number(v) })}
              />
            </Field>
            <Field label="גובה" required missing={isMissing("height")} sx={{ gridColumn: { sm: "span 3", md: "span 3" } }}>
              <NumberInput
                ariaLabel="גובה"
                value={state.height}
                placeholder="175"
                min={100}
                max={220}
                suffix="ס״מ"
                onChange={(v) => patch({ height: v === "" ? "" : Number(v) })}
              />
            </Field>
            {formulaNeedsBodyFat && (
              <Field
                label="אחוז שומן"
                required
                missing={isMissing("bodyFat")}
                sx={{ gridColumn: { sm: "span 6", md: "span 12" } }}
              >
                <NumberInput
                  ariaLabel="אחוז שומן"
                  value={state.bodyFat}
                  placeholder="18"
                  min={3}
                  max={60}
                  step={0.5}
                  suffix="%"
                  onChange={(v) => patch({ bodyFat: v === "" ? "" : Number(v) })}
                />
              </Field>
            )}
            <Field
              sx={{ gridColumn: { sm: "span 6", md: "span 4" } }}
              label="רמת פעילות (צעדים יומיים)"
            >
              <Select
                ariaLabel="רמת פעילות"
                value={state.steps}
                onChange={(v) => patch({ steps: Number(v) })}
                options={STEPS_LEVELS.map((s) => ({ value: s.value, label: s.label }))}
              />
            </Field>
            <Field
              sx={{ gridColumn: { sm: "span 6", md: "span 4" } }}
              label="אימוני כוח בשבוע"
            >
              <Select
                ariaLabel="אימוני כוח בשבוע"
                value={state.workouts}
                onChange={(v) => patch({ workouts: Number(v) })}
                options={[0, 1, 2, 3, 4, 5, 6, 7].map((n) => ({ value: n, label: `${n} אימונים` }))}
              />
            </Field>
            <Field
              sx={{ gridColumn: { sm: "span 6", md: "span 4" } }}
              label="משך אימון ממוצע"
            >
              <Select
                ariaLabel="משך אימון ממוצע"
                value={state.workoutDuration}
                onChange={(v) => patch({ workoutDuration: Number(v) })}
                options={WORKOUT_DURATIONS.map((n) => ({ value: n, label: `${n} דקות` }))}
              />
            </Field>
          </Box>

          <Box sx={{ height: "1px", background: v2.line, margin: "14px 0" }} />

          <SectionTitle>נוסחת חישוב BMR</SectionTitle>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "12px" }}>
            {BMR_FORMULAS.map((f) => (
              <Pill key={f.value} active={state.formula === f.value} onClick={() => patch({ formula: f.value })}>
                {f.label}
              </Pill>
            ))}
          </Box>
          {/* Until the formula has what it needs there is no TDEE, no target and no macro
              recommendation — so say that in place of a row of dashes, and name the fields
              that are still empty. */}
          {missing.length > 0 ? (
            /* Red only when the empty fields actually STOP the trainer. Where the calculator can
               be walked past, the same fields are a note about what they would buy — dressing
               that as an error told a trainer who never wanted a calculation that they had done
               something wrong. */
            <Box
              data-testid="calculator-missing"
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "15px 18px",
                borderRadius: "14px",
                border: `1px solid ${blocked ? v2.over : v2.line}`,
                background: blocked ? v2.overWash : v2.lineSoft,
              }}
            >
              <WarningIcon
                sx={{ fontSize: 20, color: blocked ? v2.over : v2.ink3, marginTop: "1px" }}
              />
              <Box>
                <Box sx={{ fontSize: "14px", fontWeight: 700, color: blocked ? v2.over : v2.ink }}>
                  יש למלא {MISSING_LABELS[missing[0]]}
                  {missing.slice(1).map((key) => ` · ${MISSING_LABELS[key]}`)}
                </Box>
                <Box
                  sx={{
                    fontSize: "12.5px",
                    color: blocked ? v2.over : v2.ink3,
                    marginTop: "3px",
                    lineHeight: 1.4,
                  }}
                >
                  בלי הנתונים האלה אי אפשר לחשב TDEE, {inPoints ? `יעד ${energyLabel(mode)}` : "יעד קלורי"} או המלצת מקרו
                  {formulaNeedsBodyFat ? " — הנוסחה שנבחרה מבוססת על מסת גוף רזה" : ""}.
                  {!blocked && skipCopy.canContinue}
                </Box>
              </Box>
            </Box>
          ) : (
            <TdeeBreakdown energy={energy} />
          )}

          {/*
            The goal sits directly under the TDEE it is subtracted from. These are one sentence —
            "you burn 2,235, you want to lose 0.5kg a week, so eat 1,685" — and they were split
            across two columns, with the middle term on the far side of a divider from both ends.
          */}
          <Box sx={{ height: "1px", background: v2.line, margin: "14px 0" }} />

          <SectionTitle>מטרה וקצב שינוי</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            <Field
              label="מטרה"
            >
              <Select
                ariaLabel="מטרה"
                value={state.goal}
                onChange={onGoalChange}
                options={GoalTypes.map((g) => ({ value: g.value, label: g.name }))}
              />
            </Field>
            <Field
              label={getRateLabel(state.goal)}
            >
              <Select
                ariaLabel={getRateLabel(state.goal)}
                value={state.weeklyRange}
                onChange={(v) => patch({ weeklyRange: v, customTarget: null })}
                options={getWeeklyRangesForGoal(state.goal).map((r) => ({ value: r.value, label: r.name }))}
              />
            </Field>
          </Box>

          {/* target box */}
          <Box
            sx={{
              marginTop: "10px",
              padding: "14px 18px",
              borderRadius: "14px",
              border: "1px solid rgba(1,187,167,.25)",
              background: "rgba(1,187,167,.04)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <Box sx={{ fontSize: "14px", fontWeight: 700, color: v2.ink }}>{targetName}</Box>
                {/* Overriding carbs by hand unhooks them from "fill the remainder", so the
                    macros stop adding up to the target. Say by how much, in red, right
                    next to the target — the trainer can leave it that way, but not by
                    accident. */}
                {macroGap !== 0 && (
                  <Box
                    data-testid="macro-gap"
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      padding: "2px 10px",
                      borderRadius: "20px",
                      color: v2.over,
                      background: v2.overWash,
                      border: `1px solid ${v2.over}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {macroGap > 0 ? "+" : "−"}
                    {fmt(Math.abs(macroGap), mode)} {energyUnit(mode)} {macroGap > 0 ? "מעל היעד" : "מתחת ליעד"}
                  </Box>
                )}
                {state.customTarget === null && (
                  <Box
                    sx={{
                      fontSize: "13px",
                      fontWeight: 700,
                      padding: "2px 10px",
                      borderRadius: "20px",
                      color: v2.brand,
                      background: v2.brandWash,
                      border: "1px solid rgba(1,187,167,.2)",
                    }}
                  >
                    המלצת מערכת
                  </Box>
                )}
                {editingTarget || state.customTarget !== null ? (
                  <TextButton
                    tone="neutral"
                    sx={{ padding: "2px 8px", fontSize: "13px" }}
                    onClick={() => {
                      setEditingTarget(false);
                      patch({ customTarget: null });
                    }}
                  >
                    <ResetIcon sx={{ fontSize: 14 }} />
                    איפוס
                  </TextButton>
                ) : (
                  <TextButton
                    tone="neutral"
                    sx={{ padding: "2px 8px", fontSize: "13px" }}
                    onClick={() => setEditingTarget(true)}
                  >
                    ערוך
                  </TextButton>
                )}
              </Box>
              <Box
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: missing.length > 0 ? v2.over : v2.ink2,
                  marginTop: "2px",
                }}
              >
                {missing.length > 0 ? "ממתין לנתוני הלקוח" : targetSub}
              </Box>
            </Box>
            {editingTarget ? (
              <Box sx={{ width: "120px" }}>
                {inPoints ? (
                  // typed in points, stored in kcal — the draft keeps a half-typed "18." intact
                  <Box onBlur={() => setTargetDraft(null)}>
                    <NumberInput
                      ariaLabel={targetName}
                      value={targetDraft ?? energyToInput(state.customTarget ?? calculatedTarget, mode)}
                      min={8}
                      max={60}
                      step={0.1}
                      onChange={(v) => {
                        setTargetDraft(v);
                        patch({ customTarget: v === "" ? null : energyFromInput(Number(v), mode) });
                      }}
                    />
                  </Box>
                ) : (
                  <NumberInput
                    ariaLabel="יעד קלורי יומי"
                    value={state.customTarget ?? calculatedTarget}
                    min={800}
                    max={6000}
                    step={50}
                    onChange={(v) => patch({ customTarget: v === "" ? null : Number(v) })}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                <Box
                  sx={{ fontSize: "28px", fontWeight: 800, letterSpacing: "-.8px", color: v2.brand }}
                  data-testid="target-calories"
                >
                  {fmt(target, mode)}
                </Box>
                <Box sx={{ fontSize: "12px", color: v2.ink2 }}>{energyUnit(mode)} / יום</Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* ── left column: the macro recommendation. The faint grey is the mock's
               `.panel-right` surface (#fafafa) — light enough that the white macro cards
               still read as cards on top of it. ── */}
        <Box sx={{ padding: "22px 26px", background: SURFACE }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <SectionTitle sx={{ marginBottom: 0 }}>המלצת מקרונוטריאנטים</SectionTitle>
            <TextButton
              tone="green"
              sx={{ padding: "4px 12px", fontSize: "13px", fontWeight: 700, background: v2.brandWash }}
              onClick={() =>
                patch({
                  proteinPerKg: PROTEIN_RANGE.default,
                  fatPerKg: FAT_RANGE.default,
                  carbsPerKg: CARB_RANGE.default,
                  carbsOverride: false,
                })
              }
            >
              <ResetIcon sx={{ fontSize: 14 }} />
              המלצת מערכת
            </TextButton>
          </Box>

          {!weight && (
            <Box sx={{ fontSize: "14px", color: v2.ink3, marginBottom: "10px" }}>
              הזן משקל כדי לקבל המלצת מקרו.
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <MacroSliderRow
              labelEn="Protein"
              labelHe="חלבון"
              recommendation={`המלצה: ${PROTEIN_RANGE.default} ג׳/ק״ג`}
              color={macro.protein.text}
              value={macroToInput("protein", macros.proteinG, mode)}
              unit={macroUnitPerDay}
              stats={perKgStats(state.proteinPerKg, macros.proteinKcal, macros.proteinPct, mode)}
              sliderValue={state.proteinPerKg}
              sliderLabel="חלבון גרם לקילוגרם"
              min={PROTEIN_RANGE.min}
              max={PROTEIN_RANGE.max}
              step={PROTEIN_RANGE.step}
              onSliderChange={(v: number) => patch({ proteinPerKg: v })}
            />
            <MacroSliderRow
              labelEn="Fat"
              labelHe="שומן"
              recommendation={`המלצה: ${FAT_RANGE.default} ג׳/ק״ג`}
              color={macro.fats.text}
              value={macroToInput("fats", macros.fatG, mode)}
              unit={macroUnitPerDay}
              stats={perKgStats(state.fatPerKg, macros.fatKcal, macros.fatPct, mode)}
              sliderValue={state.fatPerKg}
              sliderLabel="שומן גרם לקילוגרם"
              min={FAT_RANGE.min}
              max={FAT_RANGE.max}
              step={FAT_RANGE.step}
              onSliderChange={(v: number) => patch({ fatPerKg: v })}
            />
            <MacroSliderRow
              labelEn="Carbohydrates"
              labelHe="פחמימות"
              color={macro.carbs.text}
              value={macroToInput("carbs", macros.carbsG, mode)}
              unit={macroUnitPerDay}
              stats={perKgStats(
                state.carbsOverride ? state.carbsPerKg : macros.carbsPerKg,
                macros.carbsKcal,
                macros.carbsPct,
                mode
              )}
              sliderValue={state.carbsOverride ? state.carbsPerKg : macros.carbsPerKg}
              sliderLabel="פחמימות גרם לקילוגרם"
              min={CARB_RANGE.min}
              max={CARB_RANGE.max}
              step={CARB_RANGE.step}
              disabled={!state.carbsOverride}
              badge={state.carbsOverride ? "ידני" : "ממלא שארית"}
              badgeTone={state.carbsOverride ? "amber" : "neutral"}
              overrideLabel={state.carbsOverride ? "חזור לאוטומטי" : "ערוך ידנית"}
              onToggleOverride={() =>
                patch({
                  carbsOverride: !state.carbsOverride,
                  carbsPerKg: state.carbsOverride ? state.carbsPerKg : macros.carbsPerKg || CARB_RANGE.default,
                })
              }
              onSliderChange={(v: number) => patch({ carbsPerKg: v })}
            />
          </Box>

          {/* fiber — the same card anatomy as a macro, with its own stats */}
          <Box
            sx={{
              marginTop: "8px",
              border: `1px solid ${v2.line}`,
              borderInlineStart: `3px solid ${macro.fiber.text}`,
              borderRadius: "12px",
              background: v2.surface,
              padding: "10px 14px 11px",
              transition: "all .15s",
              "&:hover": { boxShadow: "0 2px 12px rgba(0,0,0,.08)" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: "10px",
                marginBottom: "9px",
              }}
            >
              <Box>
                <Box
                  sx={{
                    fontSize: "11.5px",
                    fontWeight: 700,
                    letterSpacing: ".5px",
                    textTransform: "uppercase",
                    color: macro.fiber.text,
                    opacity: 0.75,
                    marginBottom: "1px",
                  }}
                >
                  Dietary Fiber
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
                  <Box sx={{ fontSize: "14.5px", fontWeight: 600, color: v2.ink }}>סיבים תזונתיים</Box>
                  <Box
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      padding: "1px 7px",
                      borderRadius: "20px",
                      background: macro.fiber.soft,
                      color: macro.fiber.text,
                      border: `1px solid ${macro.fiber.soft}`,
                    }}
                  >
                    המלצה: {defaultFiber(state.gender)} ג׳
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: "3px" }}>
                <Box
                  sx={{
                    fontSize: "24px",
                    fontWeight: 800,
                    letterSpacing: "-.5px",
                    lineHeight: 1,
                    color: macro.fiber.text,
                  }}
                >
                  {state.fiber}
                </Box>
                <Box sx={{ fontSize: "12px", color: v2.ink3 }}>גרם / יום</Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: "5px", marginBottom: "9px" }}>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  background: v2.canvas,
                }}
              >
                <Box sx={{ fontSize: "13px", color: v2.ink2 }}>יעד</Box>
                <Box sx={{ fontSize: "15px", fontWeight: 700, color: v2.ink }}>{state.fiber} ג׳</Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  background: v2.canvas,
                }}
              >
                <Box sx={{ fontSize: "13px", color: v2.ink2 }}>ברירת מחדל</Box>
                <Box sx={{ fontSize: "15px", fontWeight: 700, color: v2.ink }}>
                  {defaultFiber(state.gender)} ג׳
                </Box>
              </Box>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <TextButton
                  tone="green"
                  sx={{ padding: "3px 9px", fontSize: "12px", background: v2.brandWash }}
                  onClick={() => patch({ fiber: defaultFiber(state.gender) })}
                >
                  <ResetIcon sx={{ fontSize: 13 }} />
                  ברירת מחדל
                </TextButton>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ fontSize: "11.5px", color: v2.ink3, fontWeight: 500, minWidth: "16px" }}>
                {FIBER_RANGE.min}
              </Box>
              <Box
                component="input"
                type="range"
                aria-label="סיבים תזונתיים"
                min={FIBER_RANGE.min}
                max={FIBER_RANGE.max}
                step={FIBER_RANGE.step}
                value={state.fiber}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => patch({ fiber: Number(e.target.value) })}
                sx={{ flex: 1, height: "3px", borderRadius: "2px", accentColor: macro.fiber.text, cursor: "pointer" }}
              />
              <Box sx={{ fontSize: "11.5px", color: v2.ink3, fontWeight: 500, minWidth: "16px" }}>
                {FIBER_RANGE.max}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {onConfirm && (
        <Box
          sx={{
            padding: "14px 28px",
            borderTop: `1px solid ${v2.line}`,
            background: SURFACE,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          {/* ביטול and the way past the calculator sit at the start, the ways THROUGH it at the end */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {onCancel && (
              <TextButton sx={{ fontSize: "14px" }} onClick={onCancel}>
                ביטול
              </TextButton>
            )}
            {skipActions.length > 0 && (
              <TextButton
                sx={{ fontSize: "14px" }}
                data-testid="skip-calculator"
                aria-haspopup={skipActions.length > 1 ? "menu" : undefined}
                // One way past goes straight through; several have to ask which, because the
                // answer is what the thing being created IS and the calculator does not decide it.
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  skipActions.length === 1
                    ? skipActions[0].onClick()
                    : setSkipAnchor(event.currentTarget)
                }
                title={skipCopy.skipTitle}
              >
                <SkipIcon sx={{ fontSize: 16 }} />
                דלג על המחשבון
                {skipActions.length > 1 && <ExpandIcon sx={{ fontSize: 16 }} />}
              </TextButton>
            )}
            <Menu
              anchorEl={skipAnchor}
              open={Boolean(skipAnchor)}
              onClose={() => setSkipAnchor(null)}
              PaperProps={{ sx: { borderRadius: v2.r, boxShadow: v2.shadowMenu, minWidth: "220px" } }}
            >
              {/* The same three kinds the buttons at the other end offer — named again here
                  because this end of the footer is the one that does not apply a calculation,
                  and which kind it is remains the trainer's to say. */}
              {skipActions.map((action) => (
                <MenuItem
                  key={action.label}
                  onClick={() => {
                    setSkipAnchor(null);
                    action.onClick();
                  }}
                  sx={{ fontSize: "13.5px", gap: "10px", color: v2.ink2 }}
                >
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            {blocked && (
              <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: v2.over }}>
                יש למלא את נתוני הלקוח כדי להמשיך
              </Box>
            )}
            {/* Not an error when the trainer is free to leave without a calculation — just what
                pressing one of these now means. */}
            {!blocked && missing.length > 0 && (
              <Box sx={{ fontSize: "12.5px", color: v2.ink3 }}>{skipCopy.footerHint}</Box>
            )}
            {secondaryActions.map((action) => (
              <TextButton
                key={action.label}
                tone="green"
                sx={{ fontSize: "14px" }}
                onClick={action.onClick}
                disabled={blocked}
              >
                {action.label}
              </TextButton>
            ))}
            <PrimaryButton sx={{ fontSize: "15px" }} onClick={onConfirm} disabled={blocked}>
              <CheckIcon sx={{ fontSize: 16 }} />
              {confirmLabel}
            </PrimaryButton>
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default CalculatorPanel;
